import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAskAutumn } from "../../helpers/hooks/use-ask-autumn";
import { useCrmEnabled } from "../../helpers/hooks/use-crm-enabled";
import { useApp } from "../../helpers/app-context";
import {
  useAdminChatThreads,
  useDeleteAdminChatThread,
  useInvalidateAdminChatThreads,
} from "../../helpers/hooks/use-admin-chat-threads";
import {
  fetchAdminChatThread,
  type AskAutumnQueryResult,
} from "../../helpers/api/crm";
import { ReactComponent as ChatButtonIcon } from "../../assets/icons/chat-button.svg";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  queryResults?: AskAutumnQueryResult[];
}

const SAMPLE_PROMPTS = [
  "How is Casa Delphine doing?",
  "Who's currently at risk with open tickets?",
  "Customers with failed charges in the last 30 days?",
  "Which marketing-only customers are declining?",
];

function genId(): string {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function QueryChip({ q }: { q: AskAutumnQueryResult }) {
  const cls = q.ok
    ? "bg-green-50 text-green-700 border-green-200"
    : "bg-red-50 text-red-700 border-red-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}
      title={q.ok ? "succeeded" : (q.error ?? "failed")}
    >
      {q.ok ? "✓" : "✗"} {q.tool}
    </span>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * Ask — internal CS chatbot. Floating launcher when closed, inline panel
 * when open. Phase 8 adds server-persisted thread history: every send
 * lands in an `admin_chat_thread` row owned by the admin user, so prior
 * conversations survive refreshes / browser switches and follow-up turns
 * have full prior-turn context fed to the planner + synthesizer.
 *
 * Mirrors the autumn-frontend chat-v2 thread pattern but is intentionally
 * a separate, lighter implementation (no streaming, no widget state, no
 * tool-approval flow — Ask Autumn batches synchronously).
 */
export function AskAutumnWidget() {
  const crmEnabled = useCrmEnabled();
  const { isChatBotOpen, setIsChatBotOpen } = useApp() as {
    isChatBotOpen: boolean;
    setIsChatBotOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  };

  // Conversation state — `currentThreadId` is null for a fresh "New chat"
  // until the first send returns a server-assigned thread id.
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [view, setView] = useState<"chat" | "history">("chat");

  const mutation = useAskAutumn();
  const scrollRef = useRef<HTMLDivElement>(null);

  const threadList = useAdminChatThreads({ enabled: isChatBotOpen });
  const deleteThread = useDeleteAdminChatThread();
  const invalidateThreads = useInvalidateAdminChatThreads();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, mutation.isPending, view]);

  if (!crmEnabled) return null;

  // ─── Actions ──────────────────────────────────────────────────────────

  const startNewChat = () => {
    setCurrentThreadId(null);
    setMessages([]);
    setInput("");
    setView("chat");
  };

  const openThread = async (threadId: string) => {
    try {
      const detail = await fetchAdminChatThread(threadId);
      setCurrentThreadId(detail.thread.id);
      setMessages(
        detail.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          queryResults: m.queryResults,
        })),
      );
      setView("chat");
    } catch (err) {
      // Surface the error inline rather than swallowing — admins can see
      // when their session has expired or the thread was deleted elsewhere.
      setMessages([
        {
          id: genId(),
          role: "assistant",
          content: `**Could not load thread.** Try refreshing.`,
        },
      ]);
      setView("chat");
    }
  };

  const handleDeleteThread = (
    e: React.MouseEvent,
    threadId: string,
  ) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    deleteThread.mutate(threadId, {
      onSuccess: () => {
        if (threadId === currentThreadId) startNewChat();
      },
    });
  };

  const handleSend = (explicit?: string) => {
    const text = (explicit ?? input).trim();
    if (!text || mutation.isPending) return;

    const userMsg: ChatMessage = {
      id: genId(),
      role: "user",
      content: text,
    };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setInput("");

    mutation.mutate(
      {
        threadId: currentThreadId ?? undefined,
        messages: nextHistory.map((m) => ({
          role: m.role,
          content: m.content,
        })),
      },
      {
        onSuccess: (res) => {
          // Pin to the server-assigned id so subsequent turns continue the thread.
          if (!currentThreadId) setCurrentThreadId(res.threadId);
          setMessages((prev) => [
            ...prev,
            {
              id: genId(),
              role: "assistant",
              content: res.text || "_No response. Try rephrasing._",
              queryResults: res.queryResults,
            },
          ]);
          // Refresh the sidebar so the new (or bumped) thread shows up.
          invalidateThreads();
        },
        onError: (err: any) => {
          const serverMsg =
            err?.response?.data?.message ?? err?.message ?? "Request failed.";
          setMessages((prev) => [
            ...prev,
            {
              id: genId(),
              role: "assistant",
              content: `**Error:** ${serverMsg}`,
            },
          ]);
        },
      },
    );
  };

  // ─── Collapsed — floating launcher ─────────────────────────────────────

  if (!isChatBotOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsChatBotOpen(true)}
        aria-label="Open Ask"
        className="fixed bottom-5 right-5 z-40 rounded-full shadow-lg hover:scale-105 transition-transform"
      >
        <ChatButtonIcon />
      </button>
    );
  }

  // ─── Expanded — inline flex column (shrinks the page) ─────────────────

  const isEmpty = messages.length === 0;
  const threads = threadList.data?.threads ?? [];

  return (
    <aside
      className="flex-shrink-0 w-full lg:w-[480px] h-screen sticky top-0 bg-surface-primary border-l border-border-secondary flex flex-col"
      role="dialog"
      aria-label="Ask"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border-secondary">
        <div>
          <h2 className="text-base font-semibold text-content-primary">
            Ask
          </h2>
          <p className="text-[11px] text-content-tertiary">
            Internal CS assistant
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={startNewChat}
            className="text-[11px] text-content-secondary hover:text-content-primary px-2 py-1 rounded hover:bg-surface-secondary"
            title="Start new conversation"
          >
            + New
          </button>
          <button
            type="button"
            onClick={() => setView(view === "history" ? "chat" : "history")}
            className={`text-[11px] px-2 py-1 rounded hover:bg-surface-secondary ${
              view === "history"
                ? "text-content-primary bg-surface-secondary"
                : "text-content-secondary hover:text-content-primary"
            }`}
            title="Show past conversations"
          >
            History
          </button>
          <button
            type="button"
            onClick={() => setIsChatBotOpen(false)}
            aria-label="Close"
            className="text-content-tertiary hover:text-content-primary text-lg leading-none px-2 py-1 rounded hover:bg-surface-secondary"
          >
            ×
          </button>
        </div>
      </div>

      {/* Body — switches between chat view and history view */}
      {view === "history" ? (
        <div className="flex-1 overflow-y-auto px-3 py-3">
          {threadList.isLoading ? (
            <div className="text-xs text-content-tertiary px-2 py-4">
              Loading…
            </div>
          ) : threads.length === 0 ? (
            <div className="text-xs text-content-tertiary px-2 py-4">
              No conversations yet. Ask something below to start.
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => openThread(t.id)}
                    className={`group w-full text-left px-3 py-2 rounded-md border transition-colors ${
                      t.id === currentThreadId
                        ? "border-brand-primary bg-surface-secondary"
                        : "border-border-secondary hover:bg-surface-secondary"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-content-primary truncate">
                          {t.title}
                        </div>
                        <div className="text-[10px] text-content-tertiary mt-0.5">
                          {t.messageCount} message
                          {t.messageCount === 1 ? "" : "s"} ·{" "}
                          {relativeTime(t.lastMessageAt)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteThread(e, t.id)}
                        className="opacity-0 group-hover:opacity-100 text-content-tertiary hover:text-red-600 text-xs px-1"
                        title="Delete conversation"
                        aria-label="Delete conversation"
                      >
                        ×
                      </button>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        >
          {isEmpty && (
            <div className="pt-2">
              <h3 className="text-xs font-medium text-content-secondary mb-2">
                Try one of these
              </h3>
              <div className="flex flex-col gap-1.5">
                {SAMPLE_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => handleSend(p)}
                    className="text-left text-xs px-3 py-2 rounded-md border border-border-secondary bg-surface-primary hover:bg-surface-secondary text-content-secondary"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-brand-primary text-white whitespace-pre-wrap break-words">
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={m.id} className="flex flex-col gap-1.5">
                <div className="max-w-[95%] rounded-lg px-3.5 py-2.5 bg-surface-secondary border border-border-primary">
                  <div className="prose prose-sm max-w-none text-content-primary">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {m.content}
                    </ReactMarkdown>
                  </div>
                </div>
                {m.queryResults && m.queryResults.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {m.queryResults.map((q, i) => (
                      <QueryChip key={`${m.id}-q-${i}`} q={q} />
                    ))}
                  </div>
                )}
              </div>
            ),
          )}

          {mutation.isPending && (
            <div className="flex items-center gap-2 text-xs text-content-tertiary pl-1">
              <span className="inline-block w-3 h-3 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
              <span>Thinking…</span>
            </div>
          )}
        </div>
      )}

      {/* Composer — hidden in history view */}
      {view === "chat" && (
        <div className="border-t border-border-secondary px-4 py-3 bg-surface-primary">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about a customer, a metric, or the portfolio…"
              rows={1}
              disabled={mutation.isPending}
              className="flex-1 resize-none px-3 py-2 text-sm rounded-md border border-border-secondary bg-surface-primary text-content-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => handleSend()}
              disabled={mutation.isPending || !input.trim()}
              className="shrink-0 px-3 py-2 text-sm font-medium text-white bg-brand-primary rounded-md hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
